import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import {
  DOWNLOAD_FORMATS,
  downloadCertificate,
} from "../../../utilities/certificateDownload";
import "./DownloadCertificateMenu.scss";

const ImageFormatIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <rect
      x="3"
      y="4"
      width="18"
      height="16"
      rx="3"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <circle cx="8.5" cy="9.5" r="1.75" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M3.5 17.5 8.8 12.9a1.8 1.8 0 0 1 2.4 0l3.2 2.9m0 0 1.7-1.5a1.8 1.8 0 0 1 2.4 0l2 1.8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const PdfFormatIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M14 3v3.5a1.5 1.5 0 0 0 1.5 1.5H19"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M8.5 16.5v-4h1.2a1.3 1.3 0 0 1 0 2.6H8.5m4.4 1.4v-4h.9a1.6 1.6 0 0 1 1.6 1.6v.8a1.6 1.6 0 0 1-1.6 1.6h-.9Z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FORMAT_LABELS = {
  [DOWNLOAD_FORMATS.pdf]: "PDF",
  [DOWNLOAD_FORMATS.image]: "image",
};

const FORMAT_OPTIONS = [
  {
    format: DOWNLOAD_FORMATS.image,
    label: "Image",
    hint: "High-resolution PNG",
    Icon: ImageFormatIcon,
  },
  {
    format: DOWNLOAD_FORMATS.pdf,
    label: "PDF",
    hint: "Print-ready document",
    Icon: PdfFormatIcon,
  },
];

/**
 * The download control shared by every certificate card and modal: the same
 * circular icon button as before, but it now asks for a format first.
 *
 * `onBeforeDownload` is how the card-level buttons keep their old one-click
 * feel - they open the modal so the artwork this captures is on screen.
 */
const DownloadCertificateMenu = ({
  name,
  icon,
  onBeforeDownload,
  align = "left",
  className = "",
  label = "Download certificate",
}) => {
  const [open, setOpen] = useState(false);
  const [busyFormat, setBusyFormat] = useState(null);
  const containerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleSelect = async (event, format) => {
    event.stopPropagation();
    if (busyFormat) return;

    setOpen(false);
    setBusyFormat(format);

    try {
      // Opens the modal on the card-level buttons, so the artwork is mounted.
      onBeforeDownload?.();
      await downloadCertificate(format, name);
      toast.success(
        `${format === DOWNLOAD_FORMATS.pdf ? "PDF" : "Image"} downloaded.`
      );
    } catch (error) {
      console.error("Certificate download failed.", error);
      toast.error("Could not prepare that download. Please try again.");
    } finally {
      if (mountedRef.current) setBusyFormat(null);
    }
  };

  return (
    <div
      className={`certDownload ${className}`}
      ref={containerRef}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className={`certDownload__trigger ${
          busyFormat ? "is-busy" : ""
        } ${open ? "is-open" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          if (!busyFormat) setOpen((prev) => !prev);
        }}
        disabled={Boolean(busyFormat)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
      >
        {icon}
      </button>

      {open && (
        <div
          className={`certDownload__menu certDownload__menu--${align}`}
          role="menu"
        >
          <span className="certDownload__menuTitle">Download as</span>
          {FORMAT_OPTIONS.map(({ format, label: optionLabel, hint, Icon }) => (
            <button
              key={format}
              type="button"
              role="menuitem"
              className="certDownload__option"
              onClick={(e) => handleSelect(e, format)}
            >
              <span className="certDownload__optionIcon">
                <Icon />
              </span>
              <span className="certDownload__optionText">
                <span className="certDownload__optionLabel">{optionLabel}</span>
                <span className="certDownload__optionHint">{hint}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Portalled to the body so it covers the certificate modal that the
          card buttons open, and blocks clicks that would close it mid-capture.
          It sits outside #certificateContainer, so it is never captured. */}
      {busyFormat &&
        createPortal(
          <div className="certDownloadProgress" role="alert" aria-busy="true">
            <div className="certDownloadProgress__card">
              <span className="certDownloadProgress__spinner" />
              <span className="certDownloadProgress__title">
                Preparing your {FORMAT_LABELS[busyFormat]}…
              </span>
              <span className="certDownloadProgress__hint">
                Your download will start in a moment.
              </span>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DownloadCertificateMenu;
